import React, {
  memo,
  useMemo,
} from 'react';

import {
  Button,
  Divider,
  Typography,
} from 'antd';
import {
  PiArrowRightBold,
  PiThumbsUp,
  PiUserPlus,
  PiUsers,
} from 'react-icons/pi';
import { TbMessage } from 'react-icons/tb';
import TMIcon from 'tm-icons-library';

import AttachedFileItem
  from '../../../../components/AttachedFileItem/AttachedFileItem';
import CustomPill from '../../../../components/CustomPill/CustomPill';
import CustomReadMoreContent
  from '../../../../components/CustomReadMoreText/CustomReadMoreContent';
import ProfileItem from '../../../../components/ProfileItem/ProfileItem';
import { useTheme } from '../../../../context/ThemeProvider';
import {
  setCreateOrViewOfferModalState,
} from '../../../../context/tqConnectServiceRequestReducer';
import { useTQConnectConfig } from '../../../../hooks/queries/tqConnectQueries';
import { downloadFileWithProgress } from '../../../../shared/fileUtils';
import { useAppDispatch } from '../../../../shared/hooks';
import {
  getTQServiceRequestReviewRating,
} from '../../../../shared/tqConnectUtils';
import {
  dynamicItemTitle,
  getReadableDateAndTimeFormat,
  getUserDisplayName,
} from '../../../../shared/utils';
import {
  ITQServiceRequestDetailedChangeLog,
  ITQServiceRequestDetails,
  TQServiceRequestChangeLog,
  TQServiceRequestChangeLogType,
} from '../../../../types/tqConnectTypes/service-requests';

const ICON_SIZE = 24;

const BUTTON_SIZE = 48;

interface IListItem {
  type: TQServiceRequestChangeLogType;
  icon: React.ReactNode;
  title: React.ReactNode;
  subtitle: React.ReactNode;
  extra?: React.ReactNode;
  content?: React.ReactNode;
}

interface Props {
  serviceRequestDetails: ITQServiceRequestDetails;
  isMyRequest: boolean;
  changeLogs: ITQServiceRequestDetailedChangeLog[];
  setOpenTQServiceRequestResponseBottpmSheet: (open: boolean) => void;
  onViewChat?: () => void;
}

const ServiceRequestChangeLogs: React.FC<Props> = ({
  serviceRequestDetails,
  isMyRequest,
  changeLogs,
  setOpenTQServiceRequestResponseBottpmSheet,
  onViewChat,
}) => {
  const { colors: COLORS } = useTheme();

  const dispatch = useAppDispatch();

  const { data: tqConnectConfigResult } = useTQConnectConfig();

  const { requestedBy, visibility, requestedTo, acceptedResponse } =
    serviceRequestDetails;

  const items: IListItem[] = useMemo(() => {
    const list: IListItem[] = [];

    // show who was requested to prior to the request being changed from private to public
    const privateRequestOpenedLog = changeLogs.find(
      (log) => log.type === TQServiceRequestChangeLog.OPEN && log?.requestedTo,
    );
    const privateRequestRequestedTo = privateRequestOpenedLog?.requestedTo;

    // show when a response is accepted and the request status is changed to in-progress
    const requestAcceptedLog = changeLogs.find(
      (log) => log.type === TQServiceRequestChangeLog.IN_PROGRESS,
    );

    // show when a private request is rejected by whom it was requested to
    const requestRejectedLog = changeLogs.find(
      (log) => log.type === TQServiceRequestChangeLog.REJECTED,
    );

    // show when a request is marked as completed
    const requestCompletedLog = changeLogs.find(
      (log) => log.type === TQServiceRequestChangeLog.COMPLETED,
    );

    // show when a request is changed from private to public after being rejected
    const visibilityChangeLog = changeLogs.find(
      (log) => log.type === TQServiceRequestChangeLog.VISIBILITY_CHANGE,
    );

    // Determine what logs to show based on conditions
    const canShowPrivateRequestOpenedLog =
      privateRequestOpenedLog && visibilityChangeLog;
    const canShowRequestAcceptedLog = Boolean(requestAcceptedLog);
    const canShowRequestRejectedLog = Boolean(requestRejectedLog);
    const canShowRequestCompletedLog = Boolean(requestCompletedLog);
    const canShowVisibilityChangeLog = Boolean(visibilityChangeLog);

    // logs order: opened -> accepted/rejected -> visibility change -> completed

    // 1. Private request opened (only show if later made public)
    if (canShowPrivateRequestOpenedLog) {
      list.push({
        type: privateRequestOpenedLog.type,
        icon: (
          <TMIcon
            name="lock-closed-outline"
            size={ICON_SIZE}
            color={COLORS.ICON}
          />
        ),
        title: (
          <>
            <Typography.Text strong>
              {isMyRequest
                ? 'You'
                : getUserDisplayName(requestedBy?._id, requestedBy?.name || '')}
            </Typography.Text>{' '}
            requested this {dynamicItemTitle('tq-offering')} from{' '}
            <Typography.Text strong>
              {getUserDisplayName(
                privateRequestRequestedTo?._id,
                privateRequestRequestedTo?.name || '',
                {
                  showReplacedByYou: true,
                  replacedByYouText: 'you',
                },
              )}
            </Typography.Text>
          </>
        ),
        subtitle: getReadableDateAndTimeFormat(
          privateRequestOpenedLog.createdAt,
        ),
      });
    }

    // 2. Request rejected
    if (
      canShowRequestRejectedLog &&
      requestRejectedLog &&
      privateRequestRequestedTo
    ) {
      const rejectedBy = privateRequestRequestedTo;
      list.push({
        type: requestRejectedLog.type,
        icon: (
          <TMIcon
            name="close-circle-outline"
            size={ICON_SIZE}
            color={COLORS.ERROR}
          />
        ),
        title: (
          <>
            <Typography.Text strong>
              {getUserDisplayName(rejectedBy?._id, rejectedBy?.name || '', {
                showReplacedByYou: true,
                replacedByYouText: 'You',
              })}
            </Typography.Text>{' '}
            rejected{' '}
            <Typography.Text strong>
              {isMyRequest
                ? 'your'
                : getUserDisplayName(
                    requestedBy?._id,
                    requestedBy?.name || '',
                    {
                      suffix: "'s",
                    },
                  )}
            </Typography.Text>{' '}
            request
          </>
        ),
        subtitle: getReadableDateAndTimeFormat(requestRejectedLog.createdAt),
        content: (
          <CustomReadMoreContent lines={2} className="logs__item__content">
            {requestRejectedLog.reason}
          </CustomReadMoreContent>
        ),
      });
    }

    // 3. Visibility change (private to public after rejection)
    if (canShowVisibilityChangeLog && visibilityChangeLog) {
      list.push({
        type: visibilityChangeLog.type,
        icon: <PiUsers size={ICON_SIZE} color={COLORS.ICON} />,
        title: <Typography.Text strong>Visibility updated</Typography.Text>,
        subtitle: (
          <Typography.Text
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            Private <PiArrowRightBold size={16} color={COLORS.TEXT2} /> Public
            on{' '}
            {getReadableDateAndTimeFormat(visibilityChangeLog.createdAt, {
              relativeTime: false,
            })}
          </Typography.Text>
        ),
      });
    }

    // 4. Request accepted
    if (canShowRequestAcceptedLog && requestAcceptedLog) {
      const responder = requestedTo;
      list.push({
        type: requestAcceptedLog.type,
        icon: <PiUserPlus size={ICON_SIZE} color={COLORS.ICON} />,
        title:
          visibility === 'public' ? (
            // Public request acceptance
            <>
              <Typography.Text strong>
                {isMyRequest
                  ? 'You'
                  : getUserDisplayName(
                      requestedBy?._id,
                      requestedBy?.name || '',
                    )}
              </Typography.Text>{' '}
              accepted help from{' '}
              <Typography.Text strong>
                {getUserDisplayName(responder?._id, responder?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'you',
                })}
              </Typography.Text>
            </>
          ) : (
            // Private request acceptance
            <>
              <Typography.Text strong>
                {getUserDisplayName(responder?._id, responder?.name || '', {
                  showReplacedByYou: true,
                  replacedByYouText: 'You',
                })}
              </Typography.Text>{' '}
              accepted{' '}
              <Typography.Text strong>
                {isMyRequest
                  ? 'your'
                  : getUserDisplayName(
                      requestedBy?._id,
                      requestedBy?.name || '',
                      {
                        suffix: "'s",
                      },
                    )}
              </Typography.Text>{' '}
              request
            </>
          ),
        subtitle: getReadableDateAndTimeFormat(requestAcceptedLog.createdAt),
        extra: acceptedResponse ? (
          <Button
            className="siteBtn grayBtn outlineBtn view-offer-btn"
            onClick={() => {
              dispatch(
                setCreateOrViewOfferModalState({
                  isOpen: true,
                  mode: 'view',
                  serviceRequestId: serviceRequestDetails._id,
                  respondedBy: acceptedResponse?.respondedBy,
                  response: acceptedResponse,
                }),
              );
              setOpenTQServiceRequestResponseBottpmSheet(true);
            }}>
            <TbMessage size={20} color={COLORS.ICON} />
            View offer
          </Button>
        ) : null,
      });
    }

    // 5. Request completed
    if (canShowRequestCompletedLog && requestCompletedLog) {
      const review = requestCompletedLog?.response?.review;
      const rating = review?.rating;
      const ratingCategory = rating
        ? getTQServiceRequestReviewRating(
            rating,
            tqConnectConfigResult?.tqConnectConfig,
          )
        : null;

      list.push({
        type: requestCompletedLog.type,
        icon: <PiThumbsUp size={ICON_SIZE} color={COLORS.SUCCESS} />,
        title: (
          <>
            <Typography.Text strong>
              {isMyRequest
                ? 'You'
                : getUserDisplayName(requestedBy?._id, requestedBy?.name || '')}
            </Typography.Text>{' '}
            marked this {dynamicItemTitle('tq-offering')} as completed and
            shared feedback
          </>
        ),
        subtitle: getReadableDateAndTimeFormat(requestCompletedLog.createdAt),
        extra: ratingCategory ? (
          <div>
            <CustomPill
              shape="pill"
              text={
                <>
                  {ratingCategory.logo}
                  <Typography.Text
                    style={{
                      color: ratingCategory.color,
                      fontSize: 14,
                      fontWeight: 500,
                      display: 'inline-block',
                      marginLeft: 6,
                    }}>
                    {ratingCategory.name}
                  </Typography.Text>
                </>
              }
              textSize={28}
              borderRadius={50}
              borderColor={ratingCategory.color}
              bgColor={COLORS.SURFACE_PAGE}
              disableHoverEffect
              style={{
                lineHeight: 1,
                paddingBlock: 6,
                paddingInline: '6px 12px',
              }}
            />
          </div>
        ) : null,
        content: review ? (
          <div className="logs__item__review__content">
            <Button
              className="siteBtn grayBtn outlineBtn view-chat-btn"
              onClick={() => onViewChat?.()}>
              <TbMessage size={20} color={COLORS.ICON} />
              View Chat
            </Button>
            <div className="logs__item__content logs__item__review">
              <CustomReadMoreContent lines={2} className="review__feedback">
                {review.feedback}
              </CustomReadMoreContent>

              <div className="review__attachments">
                {review.attachments.map((attachment) => (
                  <AttachedFileItem
                    key={attachment.url}
                    fileName={attachment.name}
                    fileSize={attachment.size}
                    fileExtension={attachment.type}
                    onDownload={() =>
                      downloadFileWithProgress(attachment.url, attachment.name)
                    }
                  />
                ))}
              </div>
            </div>
          </div>
        ) : null,
      });
    }

    return list;
  }, [
    changeLogs,
    COLORS.ICON,
    COLORS.ERROR,
    COLORS.TEXT2,
    COLORS.SUCCESS,
    COLORS.SURFACE_PAGE,
    isMyRequest,
    requestedBy?._id,
    requestedBy?.name,
    requestedTo,
    visibility,
    acceptedResponse,
    dispatch,
    serviceRequestDetails._id,
    setOpenTQServiceRequestResponseBottpmSheet,
    tqConnectConfigResult?.tqConnectConfig,
    onViewChat,
  ]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="service-request-change-logs">
      {items.map((item, index) => (
        <>
          {index > 0 && (
            <Divider
              type="vertical"
              style={{
                top: 0,
                height: 30,
                borderColor: COLORS.BORDER,
                margin: '0 32px',
              }}
            />
          )}

          <div
            key={item.type}
            className={`logs__item logs__item--${item.type}`}
            style={{
              borderColor:
                (item.type === 'completed' && COLORS.SUCCESS200) ||
                (item.type === 'rejected' && COLORS.ERROR200) ||
                COLORS.BORDER,
              backgroundColor:
                (item.type === 'completed' && COLORS.SUCCESS_FILL) ||
                (item.type === 'rejected' && COLORS.ERROR_FILL) ||
                COLORS.SURFACE_PAGE,
            }}>
            <ProfileItem
              title={item.title}
              subtitle={item.subtitle}
              avatar={
                <CustomPill
                  shape="square"
                  text={item.icon}
                  textSize={ICON_SIZE}
                  width={BUTTON_SIZE}
                  height={BUTTON_SIZE}
                  borderRadius={12}
                  borderColor={
                    (item.type === 'completed' && COLORS.SUCCESS100) ||
                    (item.type === 'rejected' && COLORS.ERROR100) ||
                    COLORS.BORDER
                  }
                  bgColor={COLORS.SURFACE_PAGE}
                  disableHoverEffect
                />
              }
              action={item.extra}
              titleStyle={{
                marginBottom: 2,
                fontSize: 14,
                fontWeight: 400,
                color: COLORS.TEXT1,
              }}
              subtitleStyle={{
                fontSize: 12,
                fontWeight: 400,
              }}
            />

            {item.content}
          </div>
        </>
      ))}
    </div>
  );
};

export default memo(ServiceRequestChangeLogs);
